
import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { logout } from '@/AppRedux/actions/auth';
import { useAppDispatch, useAppSelector } from '@/AppRedux/store/hooks';

const DEFAULT_SESSION_TIMEOUT_MINUTES = 30;
const IDLE_CHECK_INTERVAL_MS = 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'click', 'touchstart'] as const;

const getSessionTimeoutMinutes = (): number => {
  const rawTimeout = import.meta.env.VITE_SESSION_TIMEOUT_MINUTES;
  const parsedTimeout = Number(rawTimeout);

  if (!Number.isFinite(parsedTimeout) || parsedTimeout <= 0) {
    return DEFAULT_SESSION_TIMEOUT_MINUTES;
  }

  return parsedTimeout;
};

const useIdleTimer = () => {
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const lastActivityAtRef = useRef<number>(Date.now());
  const isTimedOutRef = useRef(false);
  const intervalIdRef = useRef<number | null>(null);
  const [isSessionTimeoutModalOpen, setIsSessionTimeoutModalOpen] = useState(false);

  const timeoutInMilliseconds = useMemo(
    () => getSessionTimeoutMinutes() * 60 * 1000,
    [],
  );

  const clearIdleCheckInterval = useCallback(() => {
    if (intervalIdRef.current !== null) {
      window.clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  const triggerSessionTimeout = useCallback(() => {
    if (isTimedOutRef.current) {
      return;
    }

    isTimedOutRef.current = true;
    clearIdleCheckInterval();
    setIsSessionTimeoutModalOpen(true);
  }, [clearIdleCheckInterval]);

  const syncTimeoutFromLastActivity = useCallback(() => {
    const idleDuration = Date.now() - lastActivityAtRef.current;

    if (idleDuration >= timeoutInMilliseconds) {
      triggerSessionTimeout();
    }
  }, [timeoutInMilliseconds, triggerSessionTimeout]);

  const closeSessionTimeoutModal = useCallback(() => {
    setIsSessionTimeoutModalOpen(false);
    dispatch(logout());
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      clearIdleCheckInterval();
      isTimedOutRef.current = false;
      setIsSessionTimeoutModalOpen(false);
      return;
    }

    isTimedOutRef.current = false;
    lastActivityAtRef.current = Date.now();

    const onUserActivity = () => {
      if (isTimedOutRef.current) {
        return;
      }

      lastActivityAtRef.current = Date.now();
    };

    const onVisibilityOrFocus = () => {
      if (isTimedOutRef.current) {
        return;
      }

      syncTimeoutFromLastActivity();
    };

    intervalIdRef.current = window.setInterval(syncTimeoutFromLastActivity, IDLE_CHECK_INTERVAL_MS);

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onUserActivity, { passive: true }));
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);

    return () => {
      clearIdleCheckInterval();
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onUserActivity));
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [clearIdleCheckInterval, syncTimeoutFromLastActivity, token]);

  return {
    isSessionTimeoutModalOpen,
    closeSessionTimeoutModal,
  };
};

export default useIdleTimer;
